export interface RepeaterConfig {
  useAnimationFrame: boolean;
  timeout: boolean;
  timeoutDelay: number;
  frequency: number;
  condition: (context: Repeater) => boolean;
  onRepeat: (context: Repeater) => void;
  onStart: (context: Repeater) => void;
  onEnd: (context: Repeater) => void;
}

const REPEATER_DEFAULT_CONFIG: RepeaterConfig = {
  useAnimationFrame: true,
  timeout: true,
  timeoutDelay: 10,
  frequency: 24,
  condition: () => true,
  onRepeat: () => {},
  onStart: () => {},
  onEnd: () => {},
};

export class Repeater {
  public config: RepeaterConfig;

  public duration?: number;
  public startTime?: number;
  public endTime?: number;

  public intervalId: number = 0;
  public timeoutId: number = 0;
  public animationFrameId: number = 0;

  public isActive: boolean = false;
  public count: number = 0;

  constructor(config?: Partial<RepeaterConfig>) {
    this.config = Object.assign({}, REPEATER_DEFAULT_CONFIG);

    this.setConfig(config);
  }

  public setConfig(config?: Partial<RepeaterConfig>): this {
    if (typeof config === 'object') {
      Object.assign(this.config, config);
    }

    return this;
  }

  public startTimeout(): this {
    this.timeoutId = setTimeout(
      () => this.stop(),
      1000 * this.config.timeoutDelay
    );

    return this;
  }

  public resetTimeout(): this {
    clearTimeout(this.timeoutId);

    this.startTimeout();

    return this;
  }

  private onRepeat(): this {
    this.count++;

    if (this.config.useAnimationFrame === true) {
      cancelAnimationFrame(this.animationFrameId);

      this.animationFrameId = window.requestAnimationFrame(
        () => this.config.onRepeat(this)
      );
    } else {
      this.config.onRepeat(this);
    }

    return this;
  }

  public forceStart(): this {
    if (this.isActive === true) {
      this.stop();
    }

    this.start();

    return this;
  }

  public start(): this {
    if (
      this.isActive === false
      && this.config.condition(this) === true
    ) {
      this.isActive = true;

      this.count = 0;

      this.startTime = Date.now();

      this.config.onStart(this);

      this.intervalId = setInterval(
        () => this.onRepeat(),
        1000 / this.config.frequency
      );

      if (
        this.config.timeout === true
        && typeof this.config.timeoutDelay === 'number'
        && this.config.timeoutDelay > 0
      ) {
        this.startTimeout();
      }
    }

    return this;
  }

  public stop(): this {
    if (this.isActive === true) {
      cancelAnimationFrame(this.animationFrameId);
      clearTimeout(this.timeoutId);
      clearInterval(this.intervalId);

      this.endTime = Date.now();

      if (typeof this.startTime === 'number') {
        this.duration = this.endTime - this.startTime;
      }

      this.isActive = false;

      this.config.onEnd(this);
    }

    return this;
  }
}
